import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack5';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Form, FormikProvider, useFormik } from 'formik';
// material
import { LoadingButton } from '@material-ui/lab';
import { Box, Card, Grid, Stack, Switch, TextField, Typography, FormControlLabel, Button } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SaveIcon from '@material-ui/icons/Save';
import axios from '../../../utils/axios';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
//
import Label from '../../Label';

// ----------------------------------------------------------------------

CustomerNewForm.propTypes = {
  isEdit: PropTypes.bool,
  currentCustomer: PropTypes.object
};

export default function CustomerNewForm({ isEdit, currentCustomer }) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const NewCustomerSchema = Yup.object().shape({
    name: Yup.string().required('Nombre es requerido'),
    vat: Yup.string().required('C.I.F es requerido'),
    address: Yup.string().required('La dirección es requerida'),
    city: Yup.string().required('La ciudad es requerida'),
    state: Yup.string().required('La provincia es requerida'),
    postalCode: Yup.string().required('El código postal es requerido'),
    country: Yup.string().required('El país es requerido')
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: currentCustomer?.name || '',
      vat: currentCustomer?.vat || '',
      address: currentCustomer?.address || '',
      city: currentCustomer?.city || '',
      state: currentCustomer?.state || '',
      postalCode: currentCustomer?.postalCode || '',
      country: currentCustomer?.country || 'Spain',
      phone: currentCustomer?.phone || '',
      otherPhone: currentCustomer?.otherPhone || '',
      email: currentCustomer?.email || '',
      status: currentCustomer?.status || 'active'
    },
    validationSchema: NewCustomerSchema,
    onSubmit: async (values, { setSubmitting, resetForm, setErrors }) => {
      try {
        if (isEdit) {
          await axios.put(`/api/v1/customer/${currentCustomer.id}`, {
            ...values
          });
        } else {
          await axios.post(`/api/v1/customer`, {
            ...values
          });
        }
        resetForm();
        setSubmitting(false);
        enqueueSnackbar(!isEdit ? 'Creado con éxito' : 'Actualizado con éxito', { variant: 'success' });
        navigate(PATH_DASHBOARD.customer.list);
      } catch (error) {
        enqueueSnackbar(error.message, { variant: 'warning' });
        console.error(error);
        setSubmitting(false);
        setErrors(error);
      }
    }
  });

  const { errors, values, touched, handleSubmit, isSubmitting, setFieldValue, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ py: 10, px: 3 }}>
              {isEdit && (
                <Label
                  color={values.status !== 'active' ? 'error' : 'success'}
                  sx={{ textTransform: 'uppercase', position: 'absolute', top: 24, right: 24 }}
                >
                  {values.status}
                </Label>
              )}

              {isEdit && (
                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Switch
                      onChange={(event) => setFieldValue('status', event.target.checked ? 'banned' : 'active')}
                      checked={values.status !== 'active'}
                    />
                  }
                  label={
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Banned
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Apply disable account
                      </Typography>
                    </>
                  }
                  sx={{ mx: 0, mb: 3, width: 1, justifyContent: 'space-between' }}
                />
              )}
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ p: 3 }}>
              <Stack spacing={3}>
                <FormControlLabel
                  labelPlacement="start"
                  control={<TextField disabled value={currentCustomer?.id} />}
                  label={
                    <>
                      <Typography variant="subtitle2">Cuenta Cliente</Typography>
                    </>
                  }
                  sx={{
                    ml: 0,
                    mr: 0,
                    justifyContent: 'flex-start',
                    flexDirection: 'column-reverse',
                    alignItems: 'flex-start'
                  }}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    {...getFieldProps('name')}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                  />
                  <TextField
                    fullWidth
                    label="C.I.F"
                    {...getFieldProps('vat')}
                    error={Boolean(touched.vat && errors.vat)}
                    helperText={touched.vat && errors.vat}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <TextField
                    fullWidth
                    label="Dirección"
                    {...getFieldProps('address')}
                    error={Boolean(touched.address && errors.address)}
                    helperText={touched.address && errors.address}
                  />
                  <TextField
                    fullWidth
                    label="Ciudad"
                    {...getFieldProps('city')}
                    error={Boolean(touched.city && errors.city)}
                    helperText={touched.city && errors.city}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <TextField
                    fullWidth
                    label="Provincia"
                    {...getFieldProps('state')}
                    error={Boolean(touched.state && errors.state)}
                    helperText={touched.state && errors.state}
                  />
                  <TextField
                    fullWidth
                    label="Código postal"
                    {...getFieldProps('postalCode')}
                    error={Boolean(touched.postalCode && errors.postalCode)}
                    helperText={touched.postalCode && errors.postalCode}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <TextField
                    fullWidth
                    label="País"
                    {...getFieldProps('country')}
                    error={Boolean(touched.country && errors.country)}
                    helperText={touched.country && errors.country}
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    {...getFieldProps('email')}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    {...getFieldProps('phone')}
                    error={Boolean(touched.phone && errors.phone)}
                    helperText={touched.phone && errors.phone}
                  />
                  <TextField
                    fullWidth
                    label="Teléfono"
                    {...getFieldProps('otherPhone')}
                    error={Boolean(touched.otherPhone && errors.otherPhone)}
                    helperText={touched.otherPhone && errors.otherPhone}
                  />
                </Stack>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    color="secondary"
                    variant="outlined"
                    component={RouterLink}
                    to={PATH_DASHBOARD.customer.list}
                    startIcon={<ArrowBackIcon />}
                  >
                    Volver
                  </Button>
                  <LoadingButton
                    type="submit"
                    loading={isSubmitting}
                    loadingPosition="start"
                    startIcon={<SaveIcon />}
                    variant="contained"
                  >
                    {!isEdit ? 'Crear cliente' : 'Guardar cliente'}
                  </LoadingButton>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  );
}
